import React from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import _maxBy from "lodash/maxBy";
import SplitContentContainer from '../SplitContentContainer/SplitContentContainer';
import { selectBarChartData, selectTextListData } from '../../../../utils';
import SRLStudentResponses from '../SRLStudentResponses/SRLStudentResponses';
import image from '../WidgetWrapper/images/thumbnail_srl_growth_small.png';

import s from './SRLGrowthAndLearning.module.css';

const getFormattedData = insightsData => selectBarChartData(insightsData).map(item => ({
  ...item,
  answer: item.display_text || item.answer,
}));

const getGrowthAndLearningContentGraph = (height, chartData, barKey, yAxisWidth) => {
  const fontSizeConfig = {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fill: '#242424',
  };

  const max = _maxBy(chartData || [], (item) => Number(item.student_count));
  const domain = [0, Number(max.student_count) + (Number(max.student_count) * 0.3)];// allowing a value of 30 percent over the max we can handle even until 999 as value without problems

  return (
    <ResponsiveContainer height={height}>
      <BarChart
        data={chartData}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        barSize={30}
        layout="vertical"
      >
        <Bar
          dataKey={barKey}
          fill="var(--prs-c-info-800)"
          minPointSize={0}
          label={{
            ...fontSizeConfig,
            value: barKey,
            position: 'right',
          }}
        />
        <XAxis
          type="number"
          tick={false}
          axisLine={false}
          label={{
            ...fontSizeConfig,
            value: 'total students',
            position: 'center',
            fontStyle: 'italic',
            fill: 'var(--prs-c-gray-700)',
          }}
          domain={domain}
        />
        <YAxis
          type="category"
          dataKey="answer"
          tickLine={false}
          tickMargin={5}
          width={yAxisWidth}
          tick={fontSizeConfig}
        />
        <CartesianGrid vertical={false} horizontal={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const renderStudentResponses = (prompt, studentResponses) => (
  <SRLStudentResponses
    prompt={prompt}
    studentResponses={studentResponses}
    responseLimit={2}
    lineLimit={4}
  />
);

const getSRLIntro = (insightsData, isMobileXS) => {
  let leftColumn = null;
  let rightColumn = null;
  const barData = getFormattedData(insightsData);
  const { prompt, studentResponses } = selectTextListData(insightsData);

  if (barData && barData.length) {
    const yAxisWidth = studentResponses && studentResponses.length ? 130 : 220;
    leftColumn = (
      <div>
        <div className={s.graphTitle}>Reason for enrollment</div>
        <div className={s.graphContainer}>
          {getGrowthAndLearningContentGraph(180, barData, 'student_count', yAxisWidth)}
        </div>
      </div>
    );
  }

  if (studentResponses && studentResponses.length) {
    rightColumn = (
      <div className={leftColumn === null ? s.singleColumn : ''}>
        {renderStudentResponses(prompt, studentResponses)}
      </div>
    );
  }

  return (
    <SplitContentContainer
      leftColumnContent={leftColumn}
      rightColumnContent={rightColumn}
      borderPosition="center"
      stackedVersion={isMobileXS}
    />
  );
};

const getSRLCheckpoint = (insightsData, isMobileXS) => {
  const { studentResponses } = selectTextListData(insightsData);
  if (studentResponses && studentResponses.length) {
    return (
      <div className={`${s.singleColumn} ${isMobileXS ? s.fixedHeight : ''}`}>
        {renderStudentResponses(null, studentResponses)}
      </div>
    );
  }

  return (
    <div className={s.noData}>
      <div className={s.emptyContentContainer}>
        <img src={image} alt="no content data" />
      </div>
      <div>The selected activity doesn’t have any data.</div>
    </div>
  );
};

const SRLGrowthAndLearning = ({ data, isMobileXS }) => {
  const { insightsData, isSRLIntroSurvey } = data;
  return isSRLIntroSurvey ?
    getSRLIntro(insightsData, isMobileXS) :
    getSRLCheckpoint(insightsData, isMobileXS);
};

SRLGrowthAndLearning.propTypes = {
  data: PropTypes.shape({
    insightsData: PropTypes.array.isRequired,
    isSRLIntroSurvey: PropTypes.bool,
  }),
  isMobileXS: PropTypes.bool
};

export default SRLGrowthAndLearning;
